import { useAuth0 } from '@auth0/auth0-react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Popper,
  Stack,
  styled,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from 'assets/img/icons/CloseIcon';
import SearchIcon from 'assets/img/icons/SearchIcon';
import { reset, setSearchValue } from 'core/store/claimsV2Slice';
import { useAppSelector } from 'core/store/hooks';
import { resetSourceFilter } from 'core/store/sourceFilterV2Slice';
import { exampleTestClaims } from 'data/exampleTestClaims';
import { suggestedClaims } from 'data/suggestedClaims';
import { useCheckText } from 'pages/PlaygroundV2Page/hooks/useCheckText';
import { useGenerate } from 'pages/PlaygroundV2Page/hooks/useGenerate';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { trackEvent } from 'utils/Plausible';
import { showInfo, showWarning } from 'utils/SnackbarUtils';
import { BORDER_COLOR, LAVENDER_STEEL, LIGHT_BLACK } from 'utils/theme';
import PdfUploadComponent from './PdfUploadComponent/PdfUploadComponent';

const CustomPopper = styled(Popper)({
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
});

const SearchFieldBody = () => {
  const dispatch = useDispatch();
  const isClaimLoading = useAppSelector((state) => state.claimsV2.isClaimLoading);
  const searchValue = useAppSelector((state) => state.claimsV2.searchValue);
  const checkText = useCheckText();
  const getGeneratedClaims = useGenerate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isAuthenticated } = useAuth0();

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('tablet'));

  const handleClaimCheck = useCallback(
    (value: string) => {
      if (!isAuthenticated) {
        if (exampleTestClaims.includes(value)) {
          return;
        }
        showInfo('Please log in or sign up to check a claim');
        return;
      }
      if (value.length > 0) {
        let truncatedValue;
        if (value.length > 10000) {
          truncatedValue = value.slice(0, 10000);
        } else {
          truncatedValue = value;
        }
        // Use blur to hide the keyboard on mobile, only seems to work with delay
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
        checkText(truncatedValue);
        getGeneratedClaims(truncatedValue);
        trackEvent('Claim Search Clicked');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkText, getGeneratedClaims]
  );

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    if (value.length === 0) {
      dispatch(reset());
      dispatch(resetSourceFilter());
      return;
    }
    if (value.length >= 10000) {
      showWarning(
        'Input exceeds 10000 characters, please analyze a shorter text. We are working on supporting longer texts'
      );
      dispatch(setSearchValue(value.substring(0, 9999)));
      return;
    }
    dispatch(setSearchValue(value));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleClaimCheck(searchValue);
    }
  };

  const handleOptionSelect = (event, newValue) => {
    if (newValue) {
      dispatch(setSearchValue(newValue));
      handleClaimCheck(newValue);
    }
  };

  const handleSearchClick = () => {
    handleClaimCheck(searchValue);
  };

  const filterMatchingOptions = (inputValue: string) => {
    if (inputValue?.length < 3) return []; // Show options only once the user has typed 3 characters

    const normalizedInput = inputValue?.toLowerCase().trim();

    const filtered = suggestedClaims.filter((option) =>
      option
        .toLowerCase()
        .split(/\s+/)
        .some((word) => word.startsWith(normalizedInput))
    );

    return filtered.slice(0, 4); // Limit to maximum 4 options
  };

  // Get filtered options
  const filteredOptions = filterMatchingOptions(searchValue);

  const showUploadButton = isAuthenticated && !searchValue;
  const handleParsedPdf = (parsedText: string) => {
    const truncated = parsedText.slice(0, 10000);
    dispatch(setSearchValue(truncated));
    handleClaimCheck(truncated);
  };

  useEffect(() => {
    if (isClaimLoading && searchValue) {
      const timeoutId = setTimeout(() => {
        handleClaimCheck(searchValue);
      }, 500);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Autocomplete
        freeSolo // Allow any text input, not just the dropdown options
        options={filteredOptions}
        open={filteredOptions.length > 0}
        value={searchValue}
        PopperComponent={CustomPopper}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        sx={{
          display: 'flex',
        }}
        // styling for text in auto-complete dropdown
        ListboxProps={{
          sx: {
            '& .MuiAutocomplete-option': {
              paddingLeft: '35px',
              fontWeight: 300,
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id="outlined-basic"
            placeholder="Write or paste your text here"
            variant="outlined"
            inputRef={inputRef}
            sx={{
              background: 'white',
              height: '175px',
              paddingX: { mobile: '16px', tablet: '30px', desktop: '30px' },
              paddingY: { mobile: '16px', tablet: '25px', desktop: '25px' },
              '& fieldset': { border: 'none' },
              borderTopRightRadius: '12px',
              borderTopLeftRadius: '12px',
              border: `1px solid ${BORDER_COLOR}`,
              borderBottom: '0px',
              borderBottomRightRadius: '0px',
              borderBottomLeftRadius: '0px',
              position: 'relative',
              '& .MuiInputBase-root': {
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                minHeight: '46px',
                overflowY: 'auto',
              },
            }}
            multiline
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              style: {
                padding: 0,
                fontSize: isMobileScreen ? '16px' : '18px',
                color: LIGHT_BLACK,
                alignItems: 'flex-start',
              },
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    paddingTop: {
                      mobile: '16px',
                      tablet: '25px',
                      desktop: '25px',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    divider={!isMobileScreen && <Divider orientation="vertical" flexItem />}
                  >
                    {showUploadButton && <PdfUploadComponent onParsedPdf={handleParsedPdf} />}
                    {searchValue?.length > 0 && (
                      <IconButton
                        type="button"
                        sx={{
                          width: '35px',
                          height: '35px',
                        }}
                        aria-label="delete claim"
                        onClick={() => {
                          dispatch(reset());
                          dispatch(resetSourceFilter());
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                    {!isMobileScreen && (
                      <IconButton
                        type="button"
                        sx={{
                          width: '42px',
                          height: '42px',
                          backgroundColor: LIGHT_BLACK,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        aria-label="search"
                        disabled={isClaimLoading}
                        onClick={handleSearchClick}
                      >
                        {isClaimLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                            }}
                          >
                            <CircularProgress size={24} sx={{ color: LAVENDER_STEEL }} />
                          </Box>
                        ) : (
                          <SearchIcon stroke={'white'} />
                        )}
                      </IconButton>
                    )}
                  </Stack>
                </InputAdornment>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              inputMode: 'search',
              enterKeyHint: 'search',
            }}
          />
        )}
      />
    </>
  );
};

export default SearchFieldBody;
